import { Dictionary } from 'lib/types/dictionary-types';
import { getNormalizedDictionaryColors, themeObject } from '../../../../lib';

export const MIN_BAR_WIDTH = 30;
export const MIN_BAR_WIDTH_FOR_SELECTED_CHAINS = 1600;

export interface ChainMetric {
    assets?: number;
    assetsHistory?: number;
    blobs?: number;
    blobsBytes?: number;
    consentContracts?: number;
    consentContractsHistory?: number;
    consentContractsReceipts?: number;
    smartContracts?: number;
    smartContractsHistory?: number;
    smartContractsReceipts?: number;
    color?: string;
    chain?: string;
}

export interface ChainMetrics {
    [key: string]: ChainMetric;
}

export interface ManageChainsData {
    name: string;
    value: string;
}

export const DEFAULT_METRIC: ManageChainsData = {
    name: 'Assets',
    value: 'assets',
};
export const METRICS: ManageChainsData[] = [
    {
        name: 'Assets',
        value: 'assets',
    },
    {
        name: 'Assets History',
        value: 'assetsHistory',
    },
    {
        name: 'Blobs',
        value: 'blobs',
    },
    {
        name: 'Blob Size in Bytes',
        value: 'blobsBytes',
    },
    {
        name: 'Consent Contracts',
        value: 'consentContracts',
    },
    {
        name: 'Consent Contracts History',
        value: 'consentContractsHistory',
    },
    {
        name: 'Consent Contracts Receipts',
        value: 'consentContractsReceipts',
    },
    {
        name: 'Smart Contracts',
        value: 'smartContracts',
    },
    {
        name: 'Smart Contracts History',
        value: 'smartContractsHistory',
    },
    {
        name: 'Smart Contracts Receipts',
        value: 'smartContractsReceipts',
    },
];

export const dashboardContainerStyles = {
    card: {
        border: '1px solid',
        mt: 0,
        borderRadius: '5px',
        width: '100%',
    },
    filterContainer: {
        mt: '7.22px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '15px',
    },
    filterText: {
        ...themeObject.typography.inputLabel,
        fontFamily: 'Red Hat Text',
        lineHeight: '21.17px',
        color: themeObject.palette.grayScale[800],
        textTransform: 'none',
    },
    autocomplete: { pt: 0.5 },
    autocompleteFilterByTextProps: {
        width: '220px',
        '& .MuiInputBase-root': {
            cursor: 'pointer',
            '& .MuiInputBase-input': {
                cursor: 'pointer',
            },
        },
    },
    autocompleteManageChainsProps: {
        minWidth: '417px',
        maxWidth: '484px',
        width: 'fit-content',
        '& .MuiFormControl-root': {
            width: '100%',
            '& .MuiInputBase-root': {
                pr: '50px !important',
            },
        },
    },
    dashboardContainer: {
        height: '500px',
        position: 'relative',
        overflowX: 'auto',
        overflowY: 'hidden',
        direction: 'row',
        wrap: 'nowrap',
        '&::-webkit-scrollbar': { width: '8px', height: '8px' },
        '&::-webkit-scrollbar-track': {
            display: 'none',
        },
        '&::-webkit-scrollbar-thumb': {
            background: themeObject.palette.primary[500],
            borderRadius: '100px',
        },
    },
    dashboardYAxisContainer: {
        height: '100%',
        width: '100px',
        background: themeObject.palette.background.default,
        position: 'sticky',
        left: 0,
        zIndex: 2,
        flexShrink: 0,
    },
    dashboardXAxisContainer: {
        flexGrow: 1,
        position: 'absolute',
        top: 0,
        width: '100%',
    },
    dashboardChainMetricTextStyles: {
        lineHeight: '15.88px',
        color: themeObject.palette.grayScale[700],
    },
    emptyStateContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        mt: '47px',
        mb: '47px',
    },
    emptyHeadingText: {
        ...themeObject.typography.h2,
        fontSize: '26px',
        lineHeight: '30.52px',
        color: themeObject.palette.primary[700],
    },
    emptySubHeadingText: {
        ...themeObject.typography.body.default,
        ...themeObject.typography.body.body1,
        ...themeObject.typography.body.regular,
        lineHeight: '21.17px',
        color: themeObject.palette.grayScale[700],
    },
    truncateIconStyles: {
        paddingTop: '6px',
    },
    autocompleteManageChainsListboxStyles: {
        maxHeight: '214px',
    },
    renderOptionStyles: {
        width: '100%',
    },
};

export const chartStyles = {
    chartTheme: {
        axis: {
            legend: {
                text: {
                    fontFamily: 'Source Code Pro',
                    fontSize: 16,
                    fontWeight: 500,
                    letterSpacing: 2,
                },
            },
            ticks: {
                line: {
                    stroke: '#E7ECEE',
                },
                text: {
                    fontFamily: 'Red Hat Text',
                    fontWeight: 400,
                    fontSize: 12,
                    letterSpacing: 0.4,
                    fill: '#293541',
                },
            },
        },
        legends: {
            text: {
                fontFamily: 'Red Hat Text',
                fontWeight: 400,
                fontSize: 12,
                letterSpacing: 0.4,
                fill: '#293541',
            },
        },
        grid: {
            line: {
                stroke: '#E7ECEE',
            },
        },
        labels: {
            text: {
                fontFamily: 'Red Hat Text',
                fontSize: 12,
                fontWeight: 500,
                fill: '#293541',
            },
        },
    },
};

export const normalizeChartData = (
    data: ChainMetrics,
    dictionaries: Dictionary[]
): ChainMetrics => {
    if (!data) return {};

    const colors: Record<string, string> =
        getNormalizedDictionaryColors(dictionaries);

    return Object.entries(data).reduce((acc, [chain, value]) => {
        return { ...acc, [chain]: { ...value, chain, color: colors[chain] } };
    }, {});
};
